import { useState, useEffect } from "react";
import axios from "axios";
import toast, { Toaster } from "react-hot-toast";
import {
  Box,
  Container,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  VStack,
  Spinner,
  Alert,
  AlertIcon,
  IconButton,
  HStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Button,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";

import { API_BASE } from "./config";
import {
  CopyIcon,
  EditIcon,
  SearchIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";

interface Token {
  name: string;
  symbol: string;
  assetSymbol: string;
  baseSymbol: string;
  decimals: number;
  address: string;
  isShortable: boolean;
  categories: string[];
  imageUrl: string;
  coingeckoUrl: string;
  explorerUrl: string;
  isV1Available: boolean;
  isPermitSupported: boolean;
  contractVersion: string;
  _id: string;
}

function Leaderboard() {
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editToken, setEditToken] = useState<any>(null);
  const [editValues, setEditValues] = useState({
    points: "",
    noOfTrades: "",
    volume: 0,
  });

  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });
  const [dateRange, setDateRange] = useState({
    startDate: "",
    endDate: "",
  });

  const fetchTokens = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(
        `${API_BASE}/api/admin/leaderboard?page=1&limit=100`
      );
      setTokens(res?.data?.data?.leaderboards || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Error loading tokens");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  const handleEdit = (token: any) => {
    setEditToken(token);
    setEditValues({
      points: token.points ?? "",
      noOfTrades: token.noOfTrades ?? "",
      volume: token.volume / 10 ** 18,
    });
    setIsOpen(true);
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditValues({ ...editValues, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    if (
      editValues.points === "" ||
      editValues.noOfTrades === "" ||
      // editValues.volume === "" ||
      isNaN(Number(editValues.points)) ||
      isNaN(Number(editValues.noOfTrades)) ||
      isNaN(Number(editValues.volume))
    ) {
      toast.error("All fields are required and must be valid numbers.");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API_BASE}/api/admin/leaderboard/${editToken._id}`, {
        points: editValues.points,
        noOfTrades: editValues.noOfTrades,
        volume: editValues.volume * 10 ** 18,
      });
      toast.success("Leaderboard entry updated!");
      setIsOpen(false);
      fetchTokens();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const handleSort = (key: string) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig?.key === key && sortConfig?.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const filterAndSortTokens = () => {
    let filteredTokens = [...tokens];

    if (searchTerm) {
      filteredTokens = filteredTokens.filter((token: any) =>
        token.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (dateRange.startDate || dateRange.endDate) {
      filteredTokens = filteredTokens.filter((token: any) => {
        if (!token.updatedAt) return false;
        const tokenDate = new Date(token.updatedAt);
        const start = dateRange.startDate
          ? new Date(dateRange.startDate)
          : new Date(0);
        const end = dateRange.endDate
          ? new Date(dateRange.endDate)
          : new Date();
        end.setHours(23, 59, 59, 999);
        return tokenDate >= start && tokenDate <= end;
      });
    }

    if (sortConfig?.key) {
      filteredTokens.sort((a: any, b: any) => {
        let aValue = a[sortConfig?.key!];
        let bValue = b[sortConfig?.key!];

        if (sortConfig?.key === "volume") {
          aValue = a.volume / 10 ** 18;
          bValue = b.volume / 10 ** 18;
        }

        if (sortConfig?.key === "updatedAt") {
          aValue = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          bValue = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
        }

        if (aValue < bValue) {
          return sortConfig?.direction === "asc" ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig?.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredTokens;
  };

  const displayTokens = filterAndSortTokens();

  return (
    <Container maxW="container.xl" py={8}>
      <Toaster position="top-right" />
      <VStack spacing={8} align="stretch">
        <Heading as="h1" size="xl" textAlign="center">
          Leaderboard
        </Heading>

        <HStack spacing={4} flexWrap="wrap">
          <InputGroup maxW="300px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.300" />
            </InputLeftElement>
            <Input
              placeholder="Search by address"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>

          <FormControl maxW="200px">
            <Input
              type="date"
              placeholder="Start Date"
              value={dateRange.startDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, startDate: e.target.value })
              }
            />
          </FormControl>

          <FormControl maxW="200px">
            <Input
              type="date"
              placeholder="End Date"
              value={dateRange.endDate}
              onChange={(e) =>
                setDateRange({ ...dateRange, endDate: e.target.value })
              }
            />
          </FormControl>

          {(searchTerm || dateRange.startDate || dateRange.endDate) && (
            <Button
              size="sm"
              onClick={() => {
                setSearchTerm("");
                setDateRange({ startDate: "", endDate: "" });
                setSortConfig({ key: null, direction: "asc" });
              }}
            >
              Clear Filters
            </Button>
          )}
        </HStack>

        <Box overflowX="auto">
          {loading ? (
            <Box textAlign="center" py={10}>
              <Spinner />
            </Box>
          ) : error ? (
            <Alert status="error">
              <AlertIcon />
              {error}
            </Alert>
          ) : (
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Index</Th>
                  <Th>Address</Th>
                  <Th>Points</Th>
                  <Th>Tier</Th>
                  <Th>Number of Trades</Th>
                  <Th cursor="pointer" onClick={() => handleSort("volume")}>
                    <HStack spacing={1}>
                      <span>Volume</span>
                      {sortConfig?.key === "volume" &&
                        (sortConfig?.direction === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        ))}
                    </HStack>
                  </Th>
                  <Th cursor="pointer" onClick={() => handleSort("updatedAt")}>
                    <HStack spacing={1}>
                      <span>Time</span>
                      {sortConfig?.key === "updatedAt" &&
                        (sortConfig?.direction === "asc" ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        ))}
                    </HStack>
                  </Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {displayTokens?.map((token: any, idx) => (
                  <Tr key={token?.address + idx}>
                    <Td>{idx + 1}</Td>
                    <Td>
                      <Box display="flex" alignItems="center">
                        <a rel="noopener noreferrer" style={{ marginRight: 8 }}>
                          {token?.address?.slice(0, 6)}...
                          {token?.address?.slice(-4)}
                        </a>
                        <CopyIcon
                          cursor="pointer"
                          onClick={() => {
                            navigator.clipboard.writeText(token?.address);
                            toast.success("Address copied!");
                          }}
                        />
                      </Box>
                    </Td>
                    <Td>{token?.points}</Td>
                    <Td>
                      {token?.points < 1000
                        ? "Rookie"
                        : token?.points < 5000
                        ? "Challenger"
                        : token?.points < 15000
                        ? "Strategist"
                        : token?.points < 50000
                        ? "Hunter"
                        : "Apex Operator"}
                    </Td>
                    <Td>{token?.noOfTrades}</Td>
                    <Td>{token?.volume / 10 ** 18}</Td>
                    <Td>
                      {token?.updatedAt
                        ? new Date(token.updatedAt).toLocaleString()
                        : ""}
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Edit token"
                          icon={<EditIcon />}
                          size="sm"
                          onClick={() => handleEdit(token)}
                        />
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          )}
        </Box>
      </VStack>

      {/* Edit Modal */}
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Leaderboard Entry</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3}>
              <FormLabel>Points</FormLabel>
              <Input
                name="points"
                type="number"
                value={editValues?.points}
                onChange={handleEditChange}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Number of Trades</FormLabel>
              <Input
                name="noOfTrades"
                type="number"
                value={editValues?.noOfTrades}
                onChange={handleEditChange}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>Volume</FormLabel>
              <Input
                name="volume"
                type="number"
                value={editValues?.volume}
                onChange={handleEditChange}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleEditSave}
              isLoading={saving}
            >
              Save
            </Button>
            <Button onClick={() => setIsOpen(false)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
}

export default Leaderboard;
